import { FC, useEffect, FormEvent } from 'react';
import { Box, Button } from '@mui/material';

import { RooftopCreateDto, RooftopsCreateDto } from '@libs/dto';
import { useAuth, useRooftop } from '@carcareers/hooks';
import { RooftopInput } from '@carcareers/components';
import { useDrawer, useForm, useSnackbar } from '@carcareers/contexts';
import { useLazyEmployerProfileQuery } from '@carcareers/store';

export interface EmployerRooftopCreateFormProps {
  onSuccess?: () => void;
}

export const EmployerRooftopCreateForm: FC<EmployerRooftopCreateFormProps> = ({ onSuccess }) => {
  const { setOpen } = useDrawer();
  const { session } = useAuth();
  const { createRooftop } = useRooftop();
  const { data, setErrors } = useForm<RooftopCreateDto>();
  const { toggleSnackbar } = useSnackbar();
  const [getEmployerProfile] = useLazyEmployerProfileQuery();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (session?.user?.id !== undefined) {
      const employerProfile = (await getEmployerProfile(session.user.id)).data;

      if (employerProfile) {
        const rooftopData: RooftopsCreateDto = {
          rooftops: data?.rooftops || [data],
          owner: employerProfile,
        };

        const callbacks = onSuccess ? { onSuccess } : undefined;
        const response = await createRooftop(rooftopData, callbacks);

        if (response?.data) {
          setOpen(false);
        } else if (response?.errors) {
          setErrors(response.errors);
        }
      }
    } else {
      toggleSnackbar('error', 'Error creating rooftop. Please contact support.');
    }
  };

  useEffect(() => {
    return () => {
      setOpen(false);
    };
  }, [setOpen]);

  return (
    <>
      <RooftopInput type="rooftops" index={0} />
      <Box
        sx={(theme) => ({
          display: 'flex',
          gap: theme.spacing(2),
          justifyContent: 'flex-end',
          [theme.breakpoints.down('sm')]: { flexDirection: 'column-reverse' },
          [theme.containerQueries('drawer').down(500)]: { flexDirection: 'column-reverse' },
        })}
      >
        <Button size="large" variant="outlined" color="secondary" onClick={() => setOpen(false)}>
          Cancel
        </Button>
        <Button size="large" variant="contained" color="primary" onClick={handleSubmit}>
          Create Rooftop
        </Button>
      </Box>
    </>
  );
};

export default EmployerRooftopCreateForm;
